// Classroom / guided-build screen (CLS-1..3, ARCH §22) — console v2 (EDU-6, §29.8).
// Four tabs: **Blueprints** (the M37 guided-build panel, unchanged: pick a lesson
// blueprint and place it as translucent phantom voxels at the aim, or start the whole
// course; watch placed-vs-plan progress, toggle the heat overlay, clear) · **Roster**
// (every connected student, raised hands first, with Watch / Go to / Bring / Freeze) ·
// **Lesson** · **Export**. Client-only (ENG-1): the roster is chrome over what the
// server sends instructors, and every console action is a server command.
import { h } from '../h';
import type { Game } from '../../boot';
import { formatProgress, formatTicks, type RosterRow } from '../roster';

/** The built-in guided-build course. Each lesson is a STR-2 schematic (DSL); the
 *  blocks are all trivially available in creative so a class can build along. */
export const LESSONS: readonly { readonly title: string; readonly dsl: string }[] = [
  { title: 'Stone Platform', dsl: 'name lesson/platform\npalette\nS = stone\nlayer\nSSS\nSSS\nSSS' },
  { title: 'Cobblestone Pillar', dsl: 'name lesson/pillar\npalette\nC = cobblestone\nlayer\nC\nlayer\nC\nlayer\nC' },
  { title: 'Plank Wall', dsl: 'name lesson/wall\npalette\nP = oak_planks\nlayer\nPPP\nlayer\nPPP\nlayer\nPPP' },
];

/** Horizontal gap (blocks) between successive lessons when the whole course is laid
 *  out at once, so the ghosts do not overlap. */
const COURSE_GAP = 6;

export function openClassroom(game: Game): void {
  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-classroom', 'data-testid': 'classroom-screen' });
  const roster = rosterTab(game);
  const lessonTab = lessonPanel(game);
  overlay.append(panel);
  document.body.append(overlay);

  const status = h('div', { class: 'bb-script-status bb-script-ok', 'data-testid': 'classroom-status' }, 'Pick a lesson, aim where it should sit, then place.');

  // ── Lesson buttons: place one blueprint at the current aim. ──
  const lessonRow = h('div', { class: 'bb-classroom-lessons' });
  LESSONS.forEach((lesson, i) => {
    const btn = h('button', { class: 'bb-btn', 'data-testid': `classroom-place-${i}` }, lesson.title) as HTMLButtonElement;
    btn.onclick = () => {
      const a = game.blueprintAim();
      const r = game.placeBlueprint(lesson.dsl, a.x, a.y, a.z);
      status.textContent = r.ok ? `Placed “${lesson.title}” (${r.cells} blocks) — build it!` : (r.error ?? 'could not place');
    };
    lessonRow.append(btn);
  });

  const courseBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'classroom-course' }, 'Start full course') as HTMLButtonElement;
  courseBtn.onclick = () => {
    const a = game.blueprintAim();
    const entries = LESSONS.map((l, i) => ({ dsl: l.dsl, x: a.x + i * COURSE_GAP, y: a.y, z: a.z }));
    const r = game.setBlueprintCourse(entries);
    status.textContent = r.ok ? `Course started — lesson 1 of ${entries.length} (${r.cells} blocks). Finish it to unlock the next.` : (r.error ?? 'could not start course');
  };

  // ── Live progress (polled while open). ──
  const progressText = h('div', { class: 'bb-classroom-progress', 'data-testid': 'classroom-progress' }, 'No blueprint placed.');
  const barFill = h('div', { class: 'bb-classroom-bar-fill', 'data-testid': 'classroom-bar' });
  const bar = h('div', { class: 'bb-classroom-bar' }, barFill);
  const students = h('div', { class: 'bb-classroom-students', 'data-testid': 'classroom-students' });

  const refresh = (): void => {
    roster.refresh();
    const s = game.blueprintState();
    if (!s) { progressText.textContent = 'No blueprint placed.'; barFill.style.width = '0%'; students.replaceChildren(); return; }
    const pct = Math.round(s.progress * 100);
    progressText.textContent = `${s.name}: ${s.counts.correct}/${s.counts.total} placed${s.counts.wrong ? `, ${s.counts.wrong} wrong` : ''}${s.complete ? ' — complete ✓' : ` (${pct}%)`}`;
    barFill.style.width = `${pct}%`;
    barFill.classList.toggle('bb-classroom-done', s.complete);
    students.replaceChildren(...s.students.map((t) => h('div', { class: 'bb-classroom-student' }, `${t.student}: ${t.correct} correct${t.wrong ? `, ${t.wrong} wrong` : ''}`)));
    if (s.done.length) students.append(h('div', { class: 'bb-classroom-unlocked' }, `Completed: ${s.done.join(', ')}`));
  };
  const timer = window.setInterval(refresh, 200);
  refresh();

  // ── Heat overlay toggle + clear. ──
  const heatBtn = h('button', { class: 'bb-btn', 'data-testid': 'classroom-heat' }, 'Heat overlay: off') as HTMLButtonElement;
  heatBtn.onclick = () => { const on = game.toggleClassroomHeat(); heatBtn.textContent = `Heat overlay: ${on ? 'on' : 'off'}`; };
  const clearBtn = h('button', { class: 'bb-btn', 'data-testid': 'classroom-clear' }, 'Clear blueprint') as HTMLButtonElement;
  clearBtn.onclick = () => { game.clearBlueprint(); refresh(); status.textContent = 'Cleared.'; };

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.clearInterval(timer);
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  const blueprints = h('div', { class: 'bb-classroom-body', 'data-testid': 'console-panel-blueprints' },
    h('div', { class: 'bb-label' }, 'Lessons'),
    lessonRow,
    h('div', { class: 'bb-script-actions' }, courseBtn),
    status,
    h('div', { class: 'bb-label' }, 'Progress'),
    progressText,
    bar,
    students,
    h('div', { class: 'bb-script-actions' }, heatBtn, clearBtn),
  );
  const exportTab = exportPanel(game);
  const panels: Record<ConsoleTab, HTMLElement> = { blueprints, roster: roster.el, lesson: lessonTab, export: exportTab };
  const tabButtons = CONSOLE_TABS.map((t) => {
    const b = h('button', { class: 'bb-btn', 'data-testid': `console-tab-${t.id}` }, t.label) as HTMLButtonElement;
    b.onclick = () => show(t.id);
    return b;
  });
  const show = (id: ConsoleTab): void => {
    for (const t of CONSOLE_TABS) panels[t.id].style.display = t.id === id ? '' : 'none';
    tabButtons.forEach((b, i) => b.classList.toggle('bb-primary', CONSOLE_TABS[i]!.id === id));
  };
  show('blueprints');

  panel.append(
    h('div', { class: 'bb-window-title' }, 'Classroom — guided build'),
    h('div', { class: 'bb-script-actions' }, ...tabButtons),
    blueprints, roster.el, lessonTab, exportTab,
    h('div', { class: 'bb-script-actions' }, h('button', { class: 'bb-btn', onclick: close }, 'Close')),
  );
}

type ConsoleTab = 'blueprints' | 'roster' | 'lesson' | 'export';
const CONSOLE_TABS: readonly { id: ConsoleTab; label: string }[] = [
  { id: 'blueprints', label: 'Blueprints' }, { id: 'roster', label: 'Roster' },
  { id: 'lesson', label: 'Lesson' }, { id: 'export', label: 'Export' },
];

/** The Roster tab (§29.8): header controls for the whole room, then one row per
 *  connected student. Re-rendered on the console's poll. */
function rosterTab(game: Game): { el: HTMLElement; refresh: () => void } {
  const rows = h('div', { class: 'bb-classroom-students', 'data-testid': 'roster-rows' });
  const cmd = (text: string) => () => game.eduCommand(text);
  const eyesUp = (): void => {
    const line = window.prompt('Eyes up — message for the class:', '') ?? null;
    if (line !== null) game.eduCommand(`/eyesup ${line}`.trim());
  };
  const header = h('div', { class: 'bb-script-actions' },
    h('button', { class: 'bb-btn', 'data-testid': 'roster-freeze-all', onclick: cmd('/freeze all') }, 'Freeze all'),
    h('button', { class: 'bb-btn', 'data-testid': 'roster-unfreeze-all', onclick: cmd('/unfreeze all') }, 'Unfreeze all'),
    h('button', { class: 'bb-btn bb-primary', 'data-testid': 'roster-eyes-up', onclick: eyesUp }, 'EYES UP'),
    h('button', { class: 'bb-btn', 'data-testid': 'roster-eyes-down', onclick: cmd('/eyesdown') }, 'Eyes down'),
  );
  const renderRow = (r: RosterRow): HTMLElement => {
    const watching = game.spectatingId() === r.id;
    const watch = (): void => {
      if (watching) { game.spectate(null); game.eduCommand('/unwatch'); } else { game.spectate(r.id); game.eduCommand(`/watch ${r.name}`); }
    };
    return h('div', { class: 'bb-classroom-student', 'data-testid': `roster-row-${r.name}` },
      h('span', { 'data-testid': 'roster-hand' }, r.hand !== undefined ? `✋${r.hand} ` : ''),
      h('span', { 'data-testid': 'roster-name' }, r.name), ' · ',
      h('span', { 'data-testid': 'roster-progress' }, formatProgress(r)), ' · quiz ',
      h('span', { 'data-testid': 'roster-quiz' }, r.quizBest !== undefined ? String(r.quizBest) : '–'), ' · ',
      h('span', { 'data-testid': 'roster-time' }, formatTicks(r.ticksOnTask)), ' ',
      h('button', { class: 'bb-btn', 'data-testid': 'roster-watch', onclick: watch }, watching ? 'Unwatch' : 'Watch'),
      h('button', { class: 'bb-btn', 'data-testid': 'roster-goto', onclick: cmd(`/tp ${r.name}`) }, 'Go to'),
      h('button', { class: 'bb-btn', 'data-testid': 'roster-bring', onclick: cmd(`/bring ${r.name}`) }, 'Bring'),
      h('button', { class: 'bb-btn', 'data-testid': 'roster-freeze', onclick: cmd(`/freeze ${r.name}`) }, 'Freeze'),
      h('button', { class: 'bb-btn', 'data-testid': 'roster-unfreeze', onclick: cmd(`/unfreeze ${r.name}`) }, 'Unfreeze'),
    );
  };
  const refresh = (): void => {
    const list = game.eduRoster();
    rows.replaceChildren(...(list.length ? list.map(renderRow) : [h('div', { class: 'bb-muted', 'data-testid': 'roster-empty' }, 'No students are connected.')]));
  };
  refresh();
  return { el: h('div', { class: 'bb-classroom-body', 'data-testid': 'console-panel-roster' }, header, rows), refresh };
}

/** The Export tab (§29.9, D-TF-153): `results.json` for the world and a CSV for the
 *  running lesson — pulled from the server (instructor-only there) or built from this
 *  world in solo — downloaded like a `.tfworld`. */
function exportPanel(game: Game): HTMLElement {
  const status = h('div', { class: 'bb-muted', 'data-testid': 'console-export-status' }, 'Nothing leaves the game until you export it.');
  const save = (format: 'json' | 'csv') => async (): Promise<void> => {
    try {
      const text = await game.eduExport(format);
      const lesson = game.eduActiveLesson() ?? 'lesson';
      const file = format === 'json' ? 'results.json' : `${lesson.replace(/[^\w.-]+/g, '_')}.csv`;
      downloadText(text, file, format === 'json' ? 'application/json' : 'text/csv');
      status.textContent = `Exported ${file}.`;
    } catch (e) {
      status.textContent = (e as Error).message;
    }
  };
  return h('div', { class: 'bb-classroom-body', 'data-testid': 'console-panel-export' },
    h('div', { class: 'bb-script-actions' },
      h('button', { class: 'bb-btn', 'data-testid': 'console-export-json', onclick: save('json') }, 'Download results.json'),
      h('button', { class: 'bb-btn', 'data-testid': 'console-export-csv', onclick: save('csv') }, 'Download lesson CSV')),
    status,
  );
}

/** Trigger a browser download of `text` as `name`. */
function downloadText(text: string, name: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** The Lesson tab (§29.8): what is running, and start/stop through the server's
 *  `/lesson` commands. */
function lessonPanel(game: Game): HTMLElement {
  const active = game.eduActiveLesson();
  const list = h('div', { class: 'bb-classroom-lessons' }, ...game.eduLessons().map((l) =>
    h('button', { class: 'bb-btn', 'data-testid': `console-lesson-${l.name}`, onclick: () => game.eduCommand(`/lesson start ${l.name}`) }, l.title)));
  return h('div', { class: 'bb-classroom-body', 'data-testid': 'console-panel-lesson' },
    h('div', { class: 'bb-label' }, active ? `Running: ${active}` : 'No lesson is running in this world.'),
    list,
    h('div', { class: 'bb-script-actions' },
      h('button', { class: 'bb-btn', 'data-testid': 'console-lesson-stop', onclick: () => game.eduCommand('/lesson stop') }, 'Stop lesson')),
  );
}
